import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Divider,
  Row,
  Col,
  Select,
  Radio,
  Input,
  InputNumber,
  Switch,
  Button,
  Empty,
  Popconfirm,
  Tag,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { fetchCustomizationOptions } from "../../../features/packages/packagesThunk";

const { Title, Text } = Typography;

const CATEGORY_OPTIONS = ["HOTEL", "CAR", "ROOM", "FOOD", "TOUR", "OTHERS"];

let nextKey = 0;
export const newComponentEntry = () => ({
  key: `new-${Date.now()}-${nextKey++}`,
  category: "HOTEL",
  mode: "existing", // "existing" | "new"
  customization_option_id: null,
  newName: "",
  mediaUrl: "",
  price_delta: 0,
  is_default: false,
});

const FieldLabel = ({ children, error }) => (
  <div style={{ marginBottom: 4, fontSize: 12, color: error ? "#ff4d4f" : "#888" }}>
    {children}
    {error ? ` — ${error}` : ""}
  </div>
);

/**
 * Step 3 (Packages only) — customization options. Price delta and default
 * flag are entered on the same row as the customization itself, per the
 * product decision to not defer pricing to a later step.
 *
 * `existingLinks` (edit mode) are already-attached PackageCustomizationOption
 * rows — removing one calls the dedicated DELETE endpoint immediately, since
 * the main update endpoint only appends nested-create entries, never removes.
 * `entries` are new links being composed now; they're sent as
 * customization_options.create[] on the final Save.
 */
const ComponentsStep = ({ entries, setEntries, existingLinks, onRemoveExisting, errors = {} }) => {
  const dispatch = useDispatch();
  const { customizationOptions, customizationsLoading } = useSelector(
    (state) => state.packages,
  );

  useEffect(() => {
    dispatch(fetchCustomizationOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catalogByCategory = useMemo(() => {
    const map = {};
    (customizationOptions || []).forEach((opt) => {
      const cat = (opt.category || "OTHERS").toUpperCase();
      if (!map[cat]) map[cat] = [];
      map[cat].push(opt);
    });
    return map;
  }, [customizationOptions]);

  const updateEntry = (key, patch) =>
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...patch } : e)));

  const removeEntry = (key) =>
    setEntries((prev) => prev.filter((e) => e.key !== key));

  const addEntry = () => setEntries((prev) => [...prev, newComponentEntry()]);

  return (
    <div>
      <Title level={4}>Components</Title>
      <Text type="secondary">
        Add what's included. Mark alternatives as swappable and price the
        difference right here — it's saved as this package's customization
        options.
      </Text>

      <Divider />

      {existingLinks && existingLinks.length > 0 && (
        <>
          <Title level={5}>Already attached</Title>
          {existingLinks.map((link) => {
            const optionId = link.customization_option?.id || link.customization_option_id;
            return (
              <Row
                key={optionId}
                align="middle"
                justify="space-between"
                style={{
                  padding: 12,
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                <Col>
                  <Tag color="blue">
                    {link.customization_option?.category || "—"}
                  </Tag>
                  <strong>{link.customization_option?.name}</strong>
                  {link.is_default && <Tag style={{ marginLeft: 8 }}>Default</Tag>}
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    +{link.price_delta}
                  </Text>
                </Col>
                <Col>
                  <Popconfirm
                    title="Detach this customization from the package?"
                    onConfirm={() => onRemoveExisting(optionId)}
                  >
                    <Button danger icon={<DeleteOutlined />} size="small" />
                  </Popconfirm>
                </Col>
              </Row>
            );
          })}
          <Divider />
        </>
      )}

      <Title level={5}>{existingLinks?.length ? "Add more" : "Customizations"}</Title>

      {entries.length === 0 ? (
        <Empty description="No customizations added yet" />
      ) : (
        entries.map((entry) => {
          const catalogForCategory = catalogByCategory[entry.category] || [];
          const rowErrors = errors[entry.key] || {};
          return (
            <div
              key={entry.key}
              style={{
                padding: 16,
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                marginBottom: 12,
              }}
            >
              <Row gutter={12}>
                <Col span={6}>
                  <FieldLabel>Category</FieldLabel>
                  <Select
                    style={{ width: "100%" }}
                    value={entry.category}
                    onChange={(category) =>
                      updateEntry(entry.key, { category, customization_option_id: null })
                    }
                    options={CATEGORY_OPTIONS.map((c) => ({ value: c, label: c }))}
                  />
                </Col>

                <Col span={18}>
                  <FieldLabel>Source</FieldLabel>
                  <Radio.Group
                    value={entry.mode}
                    onChange={(e) => updateEntry(entry.key, { mode: e.target.value })}
                  >
                    <Radio.Button value="existing">Link existing</Radio.Button>
                    <Radio.Button value="new">Create new</Radio.Button>
                  </Radio.Group>
                </Col>
              </Row>

              {entry.mode === "existing" ? (
                <Row style={{ marginTop: 12 }}>
                  <Col span={24}>
                    <FieldLabel error={rowErrors.source}>Catalog option</FieldLabel>
                    <Select
                      style={{ width: "100%" }}
                      status={rowErrors.source ? "error" : undefined}
                      placeholder={
                        catalogForCategory.length
                          ? "Select a catalog option"
                          : "No catalog options in this category yet"
                      }
                      loading={customizationsLoading}
                      value={entry.customization_option_id}
                      onChange={(customization_option_id) =>
                        updateEntry(entry.key, { customization_option_id })
                      }
                      options={catalogForCategory.map((opt) => ({
                        value: opt.id,
                        label: opt.name,
                      }))}
                    />
                  </Col>
                </Row>
              ) : (
                <Row gutter={12} style={{ marginTop: 12 }}>
                  <Col span={12}>
                    <FieldLabel error={rowErrors.name}>Name</FieldLabel>
                    <Input
                      status={rowErrors.name ? "error" : undefined}
                      placeholder="e.g. Sea Breeze Resort"
                      value={entry.newName}
                      onChange={(e) => updateEntry(entry.key, { newName: e.target.value })}
                    />
                  </Col>
                  <Col span={12}>
                    <FieldLabel>Image URL (optional)</FieldLabel>
                    <Input
                      placeholder="Paste a hosted image URL"
                      value={entry.mediaUrl}
                      onChange={(e) => updateEntry(entry.key, { mediaUrl: e.target.value })}
                    />
                  </Col>
                </Row>
              )}

              <Row gutter={12} align="middle" style={{ marginTop: 12 }}>
                <Col span={10}>
                  <FieldLabel error={rowErrors.price}>Price delta</FieldLabel>
                  <InputNumber
                    style={{ width: "100%" }}
                    status={rowErrors.price ? "error" : undefined}
                    value={entry.price_delta}
                    onChange={(price_delta) => updateEntry(entry.key, { price_delta })}
                  />
                </Col>

                <Col span={10}>
                  <FieldLabel>Default</FieldLabel>
                  <Switch
                    checked={entry.is_default}
                    onChange={(is_default) => updateEntry(entry.key, { is_default })}
                  />
                </Col>

                <Col span={4}>
                  <FieldLabel>&nbsp;</FieldLabel>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeEntry(entry.key)}
                  />
                </Col>
              </Row>
            </div>
          );
        })
      )}

      <Button type="dashed" block icon={<PlusOutlined />} onClick={addEntry}>
        Add customization
      </Button>
    </div>
  );
};

export default ComponentsStep;
